import { memo, useCallback, useMemo } from "react";

import { Add } from "@mui/icons-material";
import { Button, Stack, Tooltip } from "@mui/material";

import { get } from "typedash/get";

import {
  Editable,
  Item,
} from "@/extensions/obituary/pages/detail/Story/shared";

import { useStory } from "../context";

import Column, { makeRelativesColumn } from "./Column";
import { Container as ContainerInterface, isContainer } from "./Container";
import EditableItem from "./EditableItem";
import { relativeVariantOptions } from "./Multiline";
import Singleline, {
  isSingleline,
  Singleline as SinglelineInterface,
  Variant as SinglelineVariant,
} from "./Singleline";

export function isRow(item: Item): boolean {
  return isContainer(item) && item.variant.includes("row");
}

const Edit: Editable<ContainerInterface> = ({ item, path }) => {
  const { children, hidden } = item;
  const { change, dispatch, layout, story } = useStory();

  /* The old way rows were structured were like this:
    [child, child, child]

    But now we want to do it like this for example:
    ┌───────┐───────┐───────┐
    | child | child | child |
    | child | child | child |
    └───────| child | child |
            | child |───────┘
            └───────┘

    It's a container of 1-3 rows, each containing a column
    container with children. So we check if the current
    children are singlelines, and if so, we convert them
    to the new format.
  */
  const isLegacyRow = useMemo(() => {
    return children.every(isSingleline);
  }, [children]);

  const isRelative = useMemo(
    () => item.variant === "relatives-row",
    [item.variant],
  );

  const hasSinglelineColumns = useMemo(() => {
    return children.every(
      (child) => isContainer(child) && child.children.every(isSingleline),
    );
  }, [children]);

  const columnRowCounts = useMemo(() => {
    if (!hasSinglelineColumns) return [];

    return children.map((child) => {
      if (isContainer(child)) {
        return child.children.filter(isSingleline).length;
      }

      if (isSingleline(child)) return 1;

      return 0;
    });
  }, [children, hasSinglelineColumns]);

  const childVariant = useMemo(() => {
    if (isLegacyRow) {
      const child = children.find(isSingleline);
      return child?.variant;
    } else {
      const child = children.find(isContainer);
      if (child == null) return undefined;
      const grandChild = child.children.find((c): c is SinglelineInterface =>
        isSingleline(c),
      );
      return grandChild?.variant;
    }
  }, [children]);

  const handleSplit = useCallback(
    (split: -1 | 1) => {
      const newChildren =
        split === -1
          ? children.slice(0, -1)
          : [...children, makeRelativesColumn({ variant: childVariant })];

      if (newChildren.length > 1 || isRelative) {
        change<ContainerInterface>(path, {
          children: newChildren,
        });
      } else {
        change<SinglelineInterface>(path, {
          type: "singleline",
          text: "text" in newChildren[0]! ? (newChildren[0].text ?? "") : "",
          variant: newChildren[0]!.variant as SinglelineVariant,
          id: crypto.randomUUID(),
        });
      }
    },
    [change, children, isRelative],
  );

  const handleVariantChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const variant = event.target.value;

      if (isLegacyRow) {
        change<ContainerInterface>(path, {
          children: children.map((child) => ({ ...child, variant })),
        });
      } else {
        change<ContainerInterface>(path, {
          children: (children as ContainerInterface[]).map(
            (child: ContainerInterface) => ({
              ...child,
              children: child.children.map((grandChild) => ({
                ...grandChild,
                variant,
              })),
            }),
          ),
        });
      }
    },
    [change, children, path, isLegacyRow],
  );

  const handleAddRow = useCallback(
    (index: number) => {
      let newChildren = children;
      if (isLegacyRow) {
        // convert to new format
        newChildren = children.map((child) => ({
          type: item.type,
          variant: item.variant,
          flex: "column",
          id: crypto.randomUUID(),
          children: [child],
        })) as ContainerInterface[];
      }

      if (index < 0 || index >= newChildren.length) return;
      if (newChildren.length > 3) return;
      const newChildrenFormatted = newChildren as ContainerInterface[];

      const textInput: SinglelineInterface = {
        type: "singleline",
        variant: childVariant,
        text: "",
        id: crypto.randomUUID(),
      };

      newChildrenFormatted[index] = {
        ...newChildrenFormatted[index]!,
        children: [...(newChildrenFormatted[index]?.children ?? []), textInput],
      };

      change<ContainerInterface>(path, {
        children: newChildrenFormatted,
      });
    },
    [change, children, path],
  );

  const handleDeleteColumn = useCallback(
    (index: number) => {
      // handleDeleteColumn will only be reached if we delete the
      // only item inside of a column. And if we only have one column
      // we want to delete the entire row.
      if (children.length <= 1) {
        handleRemove();
        return;
      }

      const newChildren = children.toSpliced(index, 1);
      change<ContainerInterface>(path, {
        children: newChildren,
      });
    },
    [children, change, path],
  );

  const handleRemove = useCallback(
    () => dispatch({ type: "remove", path }),
    [dispatch, path],
  );

  const hidePositionControls = useMemo(() => {
    const parentPath = path.slice(0, -1);
    const parentItems =
      parentPath.length === 0
        ? story
        : (get(story, parentPath.join(".")) as unknown);

    if (!Array.isArray(parentItems)) return false;

    const rowCount = (parentItems as Item[]).filter(isRow).length;
    return rowCount <= 1;
  }, [path, story]);

  return (
    <EditableItem
      key={item.id + "-row"}
      actions="mutate"
      gap={1}
      hidden={hidden}
      hidePositionControls={hidePositionControls}
      hideVariantSelector={isRelative}
      path={path}
      splits={children.length}
      title="Row"
      variant={childVariant}
      variants={isRelative ? relativeVariantOptions : []}
      onSplit={layout === "OBITUARY_TWO_COLUMN" ? handleSplit : null}
      onVariantChange={handleVariantChange}
    >
      <Stack
        direction="row"
        gap={hasSinglelineColumns ? 0 : 1}
        sx={{ "& > *": { flex: 1 } }}
        width="100%"
      >
        {children?.map((child, i) =>
          isSingleline(child) ? (
            <Singleline
              key={"singleline-" + child.id}
              noHeader
              item={child}
              path={[...path, "children", i]}
            />
          ) : isContainer(child) ? (
            <Column
              key={"col-" + child.id}
              noHeader
              item={child}
              path={[...path, "children", i]}
              tableLayout={
                hasSinglelineColumns
                  ? {
                      columnIndex: i,
                      columnCount: children.length,
                      columnRowCounts,
                    }
                  : undefined
              }
              onDeleteSelf={() => handleDeleteColumn(i)}
            />
          ) : null,
        )}
      </Stack>

      <Stack direction="row" gap={1} sx={{ "& > *": { flex: 1 } }} width="100%">
        {children.map((_, index) => (
          <Tooltip
            key={`add-field-${index}`}
            arrow
            placement="top"
            title={`Lägg till fält`}
          >
            <Button
              color="inherit"
              sx={{
                py: 0.5,
                bgcolor: (theme) =>
                  theme.palette.mode === "light"
                    ? "rgba(0, 0, 0, 0.06)"
                    : undefined,
                "&:hover": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "rgba(0, 0, 0, 0.22)"
                      : undefined,
                },
              }}
              variant="contained"
              onClick={() => handleAddRow(index)}
            >
              <Add />
            </Button>
          </Tooltip>
        ))}
      </Stack>
    </EditableItem>
  );
};

export default memo(Edit);
